import { useCallback, useEffect, useMemo, useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { LockIcon, Note, Spinner } from '@wyocspay/ui';
import { ApiError, api, type MethodsView, type PaymentView } from './api.js';
import { LOCALES, STRINGS, detectLanguage, stripeLocale, type Lang } from './i18n.js';
import { Shell } from './Shell.js';
import { CheckoutForm } from './CheckoutForm.js';
import { ELEMENTS_APPEARANCE } from './stripe-appearance.js';
import { StatusScreen } from './StatusScreen.js';
import { LegalModal } from './LegalModal.js';
import { Advertisement } from './Advertisement.js';

/**
 * The customer payment portal.
 *
 * The whole flow stays on this origin. Card details go straight from the
 * Payment Element into Stripe; this application never sees them, and there is
 * no endpoint here that could accept them.
 */

type Screen = 'checkout' | 'status';

// Stripe.js is loaded once per connected account. An empty account means the
// charge is on the platform account itself (a platform-owned merchant), so
// Stripe.js is loaded without a connected account.
const stripeCache = new Map<string, Promise<Stripe | null>>();
function stripeFor(publishableKey: string, account: string): Promise<Stripe | null> {
  const key = `${publishableKey}:${account}`;
  let promise = stripeCache.get(key);
  if (!promise) {
    promise = account ? loadStripe(publishableKey, { stripeAccount: account }) : loadStripe(publishableKey);
    stripeCache.set(key, promise);
  }
  return promise;
}

/**
 * The screens with almost nothing on them: loading, the landing state, and the
 * notices about a link that cannot be used.
 *
 * The main element already stretched to push the footer to the bottom of the
 * viewport, which is right for a checkout and wrong for a single sentence - the
 * sentence sat at the top of the page with several hundred pixels of empty
 * background under it, which reads as a page that failed to load rather than a
 * page with little to say. Claiming that height and centring in it puts the
 * content where the eye lands. The floor keeps it from collapsing on a short
 * viewport, and the ceiling on the inner column keeps a line of text from
 * running the full width of a desktop window.
 */
function Centred({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        flex: 1,
        minHeight: 240,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '16px 0',
      }}
    >
      <div style={{ width: '100%', maxWidth: 520 }}>{children}</div>
    </div>
  );
}

export function App() {
  /**
   * The payment link token, or null when this address was opened without one.
   *
   * Null covers more than the bare domain: the legal documents open on their
   * own paths, and a link can arrive truncated somewhere between the merchant's
   * email and the browser. The length floor is the server's own minimum, so a
   * string too short to be a token is treated as no token rather than sent off
   * to produce a validation error and a screen telling somebody their link is
   * invalid when what they are holding is not a link at all.
   */
  const token = useMemo(() => {
    const match = /^\/p\/([A-Za-z0-9_-]{20,})/.exec(window.location.pathname);
    return match ? match[1] : null;
  }, []);

  // Start in the customer's own language where we have it, and remember a
  // deliberate change — a returning customer should not have to pick twice.
  const [lang, setLang] = useState<Lang>(() => {
    const saved = localStorage.getItem('wyocspay.lang') as Lang | null;
    return saved && saved in STRINGS ? saved : detectLanguage();
  });
  const t = STRINGS[lang];
  const locale = LOCALES[lang];

  const [view, setView] = useState<PaymentView | null>(null);
  const [methods, setMethods] = useState<MethodsView | null>(null);
  const [intent, setIntent] = useState<{ clientSecret: string; account: string; key: string } | null>(null);
  const [screen, setScreen] = useState<Screen>('checkout');
  // Sub-step within checkout: 1 = choose method, 2 = confirmation. Drives both
  // the wizard indicator and which panel CheckoutForm shows.
  const [checkoutStep, setCheckoutStep] = useState<1 | 2 | 3>(1);
  const [legal, setLegal] = useState<'terms' | 'privacy' | null>(
    /^\/(terms|privacy)/.test(window.location.pathname)
      ? (window.location.pathname.slice(1) as 'terms' | 'privacy')
      : null,
  );
  const [error, setError] = useState<{ code: string; message: string } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.documentElement.lang = lang;
    localStorage.setItem('wyocspay.lang', lang);
  }, [lang]);

  const bootstrap = useCallback(async () => {
    if (!token) {
      /**
       * No link, so no request.
       *
       * Nothing on the screen this produces depends on server state, which is
       * also the reason it cannot tell anybody anything: there is no call to
       * time, no response to compare and no difference to find between this and
       * a visitor who has never been near a valid token.
       */
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const loaded = await api.load(token);
      setView(loaded);

      // A payment that is already settled goes straight to the status screen —
      // re-presenting a pay button for a paid invoice invites a double charge.
      const settled = ['SUCCEEDED', 'REFUNDED', 'PARTIALLY_REFUNDED', 'PROCESSING'].includes(
        loaded.payment.status,
      );
      if (settled) {
        setScreen('status');
        setLoading(false);
        return;
      }

      // A link whose session has run out comes back CANCELLED (the server voids
      // the intent). It is a dead link now, not a payable one - show the expired
      // notice rather than trying to open a fresh attempt.
      if (loaded.payment.status === 'CANCELLED') {
        setError({ code: 'session_expired', message: '' });
        setLoading(false);
        return;
      }

      if (!loaded.stripe.configured) {
        setError({ code: 'not_configured', message: '' });
        setLoading(false);
        return;
      }

      const [created, available] = await Promise.all([api.intent(token), api.methods(token)]);
      setIntent({
        clientSecret: created.client_secret,
        account: created.connected_account ?? '',
        key: created.publishable_key,
      });
      setMethods(available);
    } catch (err) {
      if (err instanceof ApiError) setError({ code: err.code, message: err.message });
      else setError({ code: 'unknown', message: String(err) });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  // The documents open over the checkout rather than replacing it: a customer
  // reading the terms has not abandoned the payment, and navigating away would
  // discard the form they already filled in.
  const legalModal = legal ? (
    <LegalModal doc={legal} lang={lang} onClose={() => setLegal(null)} />
  ) : null;

  // The payment chrome (session band, 1-2-3 wizard, trust badges) belongs only
  // when there is an actual payment in play: a loaded, payable link on the
  // checkout, or the result of one. The landing screen, an unusable link, and
  // the loading flash have no session, so they render bare.
  const hasPaymentSession = !!view && !error && (screen === 'status' || !!intent);

  const shell = (children: React.ReactNode) => (
    <Shell
      lang={lang}
      onLang={setLang}
      t={t}
      locale={locale}
      chrome={hasPaymentSession ? 'full' : 'bare'}
      step={screen === 'status' ? 3 : checkoutStep}
      // The session countdown (15 min from first open), not the link's own
      // validity. Absent on the status screen, where nothing is being paid.
      expiresAt={screen === 'status' ? null : view?.payment.session_expires_at ?? null}
      merchant={view?.merchant.name ?? null}
      // Optional throughout: null renders nothing, and the page is then
      // identical to what it was before merchants could upload one.
      merchantLogo={view?.merchant.logo_data_uri ?? null}
      paymentId={view?.payment.id ?? null}
      /**
       * Present on the checkout and on the status screen, which are the two
       * places a customer is either about to pay or has just paid, and absent
       * everywhere else. Both of those screens have `view` loaded - the status
       * screen is only reached after the payment has been fetched - so one
       * expression covers both without either screen having to ask for it.
       */
      assurance={view?.assurance ?? null}
      onOpenLegal={setLegal}
    >
      {children}
      {legalModal}
    </Shell>
  );

  if (loading) {
    return shell(
      <Centred>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Spinner label={t.loading} />
        </div>
      </Centred>,
    );
  }

  /**
   * Opened without a payment link.
   *
   * This is not a failure and is deliberately not dressed as one. Somebody who
   * typed the domain, followed a bookmark or clicked a link that lost its tail
   * has not done anything wrong, and telling them their link is invalid is both
   * inaccurate and the fastest way to make a payment page look broken. They are
   * told what the address is and what to ask the merchant for, in a neutral
   * tone and with no warning colour.
   *
   * A link that IS present and cannot be used - expired, cancelled, unknown -
   * is a different situation and keeps the notice below. Nothing on this screen
   * says whether any particular link exists, and nothing on it names a
   * merchant, a payment or anything else about the platform.
   */
  if (!token) {
    return shell(
      <Centred>
        <div style={{ display: 'grid', gap: 12, justifyItems: 'center', textAlign: 'center' }}>
          <span
            aria-hidden
            style={{
              display: 'grid',
              placeItems: 'center',
              width: 46,
              height: 46,
              border: '1px solid var(--wc-border)',
              background: 'var(--wc-surface)',
              color: 'var(--wc-blue)',
            }}
          >
            <LockIcon size={20} />
          </span>
          <h1 style={{ margin: 0, fontSize: 20, color: 'var(--wc-ink)' }}>{t.landingHead}</h1>
          <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.55, color: 'var(--wc-muted)' }}>
            {t.landingBody}
          </p>
          <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.55, color: 'var(--wc-muted)' }}>
            {t.landingAsk}
          </p>
        </div>
      </Centred>,
    );
  }

  if (error) {
    /**
     * A link that exists in the URL but cannot be paid.
     *
     * The wording says that the link cannot be used and what to do about it,
     * and never why in a way that would help somebody working through guessed
     * tokens: no timestamps, no merchant, no payment, and nothing that
     * separates "this was cancelled at half past two" from "this was never a
     * link". What the server distinguishes, it distinguishes to be useful to
     * the person holding a real link, which is a different audience.
     */
    // Reuses the already-translated expired-link copy for a session that ran out.
    const sessionExpired = { head: t.expiredHead, body: t.expiredBody, tone: 'warn' as const };
    const map: Record<string, { head: string; body: string; tone: 'warn' | 'danger' }> = {
      link_expired: { head: t.expiredHead, body: t.expiredBody, tone: 'warn' },
      link_revoked: { head: t.revokedHead, body: t.revokedBody, tone: 'warn' },
      not_found: { head: t.notFoundHead, body: t.notFoundBody, tone: 'warn' },
      not_configured: { head: t.unavailableHead, body: t.unavailableBody, tone: 'danger' },
      session_expired: sessionExpired,
      payment_cancelled: sessionExpired,
    };
    const shown = map[error.code] ?? {
      head: t.errorHead,
      body: error.message,
      tone: 'danger' as const,
    };
    return shell(
      <Centred>
        <Note tone={shown.tone} title={shown.head}>
          {shown.body}
        </Note>
      </Centred>,
    );
  }

  if (!view) return shell(null);

  if (screen === 'status' && token) {
    return shell(
      <StatusScreen
        token={token}
        t={t}
        locale={locale}
        merchant={view.merchant.name}
        merchantLogo={view.merchant.logo_data_uri}
        paymentId={view.payment.id}
        onRetry={() => {
          setScreen('checkout');
          setCheckoutStep(1);
          void bootstrap();
        }}
      />,
    );
  }

  if (!intent) return shell(null);

  /**
   * The advertising slot lives here rather than inside the checkout.
   *
   * Sibling of <Elements>, after it, so the banner is outside the payment form
   * in the document as well as below it on the screen. Nothing sold as
   * advertising can then end up nested inside the element the customer types a
   * card into, whatever a future edit does to the checkout layout - and a
   * screen reader walking the page reaches the payment, finishes it, and only
   * then meets something labelled as an advertisement.
   *
   * Only on the checkout screen. The confirmation screen tells a customer to
   * keep the window open while we wait on their bank, and putting something
   * there that invites a click is a poor idea however carefully it opens
   * elsewhere.
   */
  const checkout = (
    <Elements
      // `key` forces a clean remount if the intent is replaced, so the Element
      // never holds a client secret that no longer matches the payment.
      key={intent.clientSecret}
      stripe={stripeFor(intent.key, intent.account)}
      options={{
        clientSecret: intent.clientSecret,
        locale: stripeLocale(lang),
        appearance: ELEMENTS_APPEARANCE,
      }}
    >
      <CheckoutForm
        t={t}
        locale={locale}
        view={view}
        methods={methods}
        step={checkoutStep}
        expiresAt={view.payment.session_expires_at}
        onStep={setCheckoutStep}
        onSubmitted={() => setScreen('status')}
        onOpenLegal={setLegal}
      />
    </Elements>
  );

  return shell(
    <>
      {checkout}
      {token ? <Advertisement token={token} t={t} /> : null}
    </>,
  );
}
